import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { fetchThesis, type ApiRun, type ApiDecision, type ApiEvent } from '../../lib/api'
import { Page } from '../../components/layout/AppShell'
import { PageHeader } from '../../components/layout/PageHeader'
import { Badge, Panel, Spinner, ErrorState, Tabs, Table, Json, type Column, type TabDef } from '../../components/ui'
import { fmtDateTime } from '../../lib/format'
import { useTraderScripts } from '../trades/useTrades'
import type { ApiTraderScript } from '../../lib/tradesApi'
import { thesisTitle, statusTone } from './thesisShared'

type Tab = 'overview' | 'runs' | 'analysis' | 'scripts' | 'events'
const TABS: TabDef<Tab>[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'runs', label: 'Runs' },
  { key: 'analysis', label: 'Analysis' },
  { key: 'scripts', label: 'Scripts' },
  { key: 'events', label: 'Events' },
]

export function ThesisDetailPage() {
  const { thesisId } = useParams({ from: '/app/theses/$thesisId' })
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const { data, isLoading, isError } = useQuery({
    queryKey: ['thesis', thesisId],
    queryFn: () => fetchThesis(thesisId),
  })

  const thesesCrumb = { label: 'Theses', onClick: () => navigate({ to: '/theses' }) }

  if (isLoading) {
    return (
      <Page width="wide" header={<PageHeader crumbs={[thesesCrumb, { label: 'Thesis' }]} />}>
        <Spinner />
      </Page>
    )
  }
  if (isError || !data) {
    return (
      <Page width="wide" header={<PageHeader crumbs={[thesesCrumb, { label: 'Thesis' }]} />}>
        <ErrorState />
      </Page>
    )
  }

  const { thesis, runs, decisions, events } = data

  return (
    <Page
      width="wide"
      header={
        <PageHeader
          crumbs={[thesesCrumb, { label: thesisTitle(thesis) }]}
          actions={<Badge tone={statusTone(thesis.status)}>{thesis.status}</Badge>}
        />
      }
      tabs={<Tabs<Tab> tabs={TABS} active={tab} onChange={setTab} />}
    >
      {tab === 'overview' && (
        <>
          <Panel title="Seed"><Json value={thesis.seed} /></Panel>
          <Panel title="Decomposition"><Json value={thesis.decomposition} /></Panel>
        </>
      )}
      {tab === 'runs' && <Runs runs={runs} />}
      {tab === 'analysis' && <Analysis decisions={decisions} />}
      {tab === 'scripts' && <Scripts thesisId={thesisId} />}
      {tab === 'events' && <Events events={events} />}
    </Page>
  )
}

function Runs({ runs }: { runs: ApiRun[] }) {
  const navigate = useNavigate()
  const cols: Column<ApiRun>[] = [
    { key: 'agent', header: 'Agent', render: (r) => <span className="font-medium">{r.agent_name}</span> },
    { key: 'mode', header: 'Mode', render: (r) => r.run_mode },
    { key: 'status', header: 'Status', render: (r) => <Badge tone={r.status === 'failed' ? 'red' : 'neutral'}>{r.status}</Badge> },
    { key: 'started', header: 'Started', render: (r) => fmtDateTime(r.started_at) },
  ]
  return (
    <Panel title="Runs">
      <Table
        columns={cols}
        rows={runs}
        empty="No agent runs."
        onRowClick={(r) => navigate({ to: '/agents/runs/$runId', params: { runId: r.run_id } })}
      />
    </Panel>
  )
}

function Analysis({ decisions }: { decisions: ApiDecision[] }) {
  const dCols: Column<ApiDecision>[] = [
    { key: 'type', header: 'Type', render: (d) => <span className="font-medium">{d.decision_type}</span> },
    { key: 'belief', header: 'Belief', render: (d) => d.belief },
    { key: 'action', header: 'Action', render: (d) => d.action },
    { key: 'at', header: 'When', render: (d) => fmtDateTime(d.created_at) },
  ]
  return (
    <Panel title="Decisions">
      <Table columns={dCols} rows={decisions} empty="No decisions recorded." />
    </Panel>
  )
}

function Scripts({ thesisId }: { thesisId: string }) {
  const { data, isLoading, isError } = useTraderScripts()
  const scripts = (data ?? []).filter((s) => s.thesis_id === thesisId)

  if (isLoading) return <Spinner />
  if (isError) return <ErrorState message="Couldn't load scripts." />

  const cols: Column<ApiTraderScript>[] = [
    { key: 'id', header: 'Script', render: (s) => (
      <Link
        to="/theses/$thesisId/scripts/$scriptId"
        params={{ thesisId, scriptId: s.id }}
        className="font-mono text-xs text-slate-700 hover:underline"
      >
        {s.id.slice(0, 8)}
      </Link>
    ) },
    { key: 'status', header: 'Status', render: (s) => <Badge tone={s.enabled ? 'green' : 'neutral'}>{s.enabled ? 'enabled' : 'disabled'}</Badge> },
    { key: 'created', header: 'Created', render: (s) => fmtDateTime(s.created_at) },
  ]
  return (
    <Panel title="Scripts">
      <Table columns={cols} rows={scripts} empty="No scripts for this thesis." />
    </Panel>
  )
}

function Events({ events }: { events: ApiEvent[] }) {
  const cols: Column<ApiEvent>[] = [
    { key: 'type', header: 'Event', render: (e) => <Badge>{e.event_type}</Badge> },
    { key: 'payload', header: 'Payload', render: (e) => <Json value={e.payload} /> },
    { key: 'at', header: 'When', render: (e) => fmtDateTime(e.created_at) },
  ]
  return (
    <Panel title="Events">
      <Table columns={cols} rows={events} empty="No events yet." />
    </Panel>
  )
}
